
import React, { useState, useEffect, useRef } from 'react';
import TitleBlock from '../elements/TitleBlock';
import { PiHandWithdrawBold, PiHandDepositBold } from "react-icons/pi";
import { FiCopy, FiCheck } from "react-icons/fi";
import { QRCodeCanvas } from 'qrcode.react';
import { URL_API } from '../config/endpoints';
import { getTgUserId, getTgInitData } from '../tgUser';

const listExchange = {
  '1': 'MEXC',
  '2': 'ByBit',
  '3': 'KuCoin',
  '4': 'BitGet',
  '5': 'Huobi',
  '6': 'BingX',
  '7': 'OKX',
  '8': 'BitMart',
  '9': 'LBank',
  '10': 'CoinW',
  '11': 'BitForex',
  '12': 'BitFinex',
  '13': 'XT',
  '14': 'DigitalFinex',
  '15': 'ProBit',
  '16': 'Phemex',
  '17': 'Tapbit',
  '18': 'AscendEX',
  '19': 'Poloniex',
  '20': 'Coinbase',
  '21': 'Kraken'
};

const selectIconExchange = (exchangeId) =>
  listExchange[exchangeId]
    ? `/access/indexExchange/${exchangeId}.svg`
    : '/access/indexExchange/0.svg';

// Helper to resolve local coin icon path
const iconPath = (symbol) => `/access/coinIcon/${String(symbol || '').toLowerCase()}.png`;

function useCountUp(target, duration = 0.5) {
  const prevRef = useRef(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const start = prevRef.current;
    const end = target;
    if (start === end) return; // если не изменилось — выходим

    const diff = end - start;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      setCount(start + diff * progress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        prevRef.current = end; // запоминаем новое «предыдущее»
      }
    };

    requestAnimationFrame(animate);
  }, [target, duration]);

  return count;
}


// Подключите это где-нибудь в глобальных стилях:
//
// @keyframes pulse {
//   0%   { opacity: 1; }
//   50%  { opacity: 0.4; }
//   100% { opacity: 1; }
// }

// Скелетон-карточка (как было выше)
function SkeletonTokenCard() {
  const cardStyles = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: '#1F1F25', border: '1px solid #3E3E4B',
    borderRadius: 30, height: 85, marginBottom: 10, padding: '0 20px',
  };
  const skeletonBase = {
    backgroundColor: '#3E3E4B',
    borderRadius: 4,
    animation: 'pulse 1.5s infinite ease-in-out'
  };
  const circle = { ...skeletonBase, width: 32, height: 32, borderRadius: '50%' };
  const shortLine = { ...skeletonBase, width: 60, height: 14, margin: '4px 0' };
  const longLine = { ...skeletonBase, width: 80, height: 14, margin: '4px 0' };

  return (
    <div style={cardStyles}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={circle} />
        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 10 }}>
          <div style={shortLine} />
          <div style={longLine} />
        </div>
      </div>
      <div style={shortLine} />
    </div>
  );
}

function TokenCard({ currency, rawBalance, price, icon }) {
  const count = useCountUp(rawBalance, 0.5);

  const cardStyles = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: '#1F1F25', border: '1px solid #3E3E4B',
    borderRadius: 30, height: 85, marginBottom: 10, padding: '0 20px',
  };

  return (
    <div style={cardStyles}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={icon}
          alt={currency}
          style={{ height: 32, marginRight: 10 }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <strong style={{ color: 'white' }}>{currency}</strong>
          <p style={{ color: 'white', margin: 0 }}>
            {count.toFixed(6)}
          </p>
        </div>
      </div>
      <p style={{ color: 'white', margin: 0 }}>
        {(count * price).toFixed(4)}$
      </p>
    </div>
  );
}



function ProfileCard({ wsRate, wsBalance }) {
  const [tokens, setTokens] = useState(null);
  const [balance, setBalance] = useState(null);
  const [withdrawError, setWithdrawError] = useState('');
  const [confirmationText, setConfirmationText] = useState('Форма отправлена. Ожидайте связи с ботом.');
  const [withdrawSuccess, setWithdrawSuccess] = useState('');

  // Загрузка
  const [pageLoaded, setPageLoaded] = useState(false);

  // Задержка перед появлением страницы (~0.5s)

  // Состояния для модальных окон
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);

  // Слушаем события для открытия модалок извне
  useEffect(() => {
    const handleOpenDeposit = () => {
      setDepositMode('direct');
      setDepositModalOpen(true);
    };
    const handleOpenWithdraw = () => {
      setWithdrawMode('direct');
      setWithdrawModalOpen(true);
    };
    
    window.addEventListener('openDepositModal', handleOpenDeposit);
    window.addEventListener('openWithdrawModal', handleOpenWithdraw);
    
    return () => {
      window.removeEventListener('openDepositModal', handleOpenDeposit);
      window.removeEventListener('openWithdrawModal', handleOpenWithdraw);
    };
  }, []);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [depositMode, setDepositMode] = useState('direct'); // 'direct' | 'onchain'
  const [copied, setCopied] = useState(false);
  const [withdrawMode, setWithdrawMode] = useState('direct'); // 'direct' | 'onchain'

  // Состояния для формы депозита
  const [depositToken, setDepositToken] = useState('');
  const [depositDropdownOpen, setDepositDropdownOpen] = useState(false);
  const [depositSearchQuery, setDepositSearchQuery] = useState('');
  const [depositField2, setDepositField2] = useState(''); // сумма
  const [depositField3, setDepositField3] = useState(''); // сеть
  const [depositField4, setDepositField4] = useState(''); // адрес

  // Состояния для формы вывода
  const [withdrawToken, setWithdrawToken] = useState('');
  const [withdrawDropdownOpen, setWithdrawDropdownOpen] = useState(false);
  const [withdrawSearchQuery, setWithdrawSearchQuery] = useState('');
  const [withdrawField2, setWithdrawField2] = useState(''); // сумма
  const [withdrawField3, setWithdrawField3] = useState(''); // сеть
  const [withdrawField4, setWithdrawField4] = useState('');
  const [withdrawExchangeId, setWithdrawExchangeId] = useState(''); // id из listExchange
  const [withdrawExDropdownOpen, setWithdrawExDropdownOpen] = useState(false);
  const [withdrawExSearchQuery, setWithdrawExSearchQuery] = useState('');


  const validateDeposit = () => {
    const errors = {};
    if (!depositToken) errors.depositToken = 'Выберите токен';
    if (!depositField2) errors.depositField2 = 'Введите сумму';
    if (!depositField3) errors.depositField3 = 'Укажите сеть';
    if (!depositField4) errors.depositField4 = 'Укажите адрес';
    return errors;
  };

  const onSubmit = () => {
    const errors = validateDeposit();
    if (Object.keys(errors).length) {
      return; // можно показать уведомление
    }
    handleDepositSubmit({ token: depositToken, amount: depositField2, network: depositField3, address: depositField4 });
    setDepositModalOpen(false);
  };

  const isDisabled = !depositToken || !depositField2 || !depositField3 || !depositField4;

  // ...

  // Флаг валидности формы:
  const amountOk = withdrawField2 && !isNaN(parseFloat(withdrawField2)) && Number(parseFloat(withdrawField2)) > 0;
  const isWithdrawFormValid = withdrawMode === 'direct'
    ? Boolean(
        withdrawToken &&
        amountOk &&
        withdrawField3.trim() &&
        withdrawField4.trim()
      )
    : Boolean(
        withdrawToken &&
        amountOk &&
        withdrawExchangeId &&
        withdrawField4.trim()
      );


  // Стили для модальных окон
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const modalContentStyle = {
    backgroundColor: '#2A2A33',
    width: '100%',
    maxWidth: '400px',
    padding: 24,
    paddingTop: 28,
    paddingBottom: 30,
    borderRadius: 20,
    textAlign: 'center',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    border: '1px solid #3E3E4B',
  };

  // Подписанная initData — берём СВЕЖУЮ на каждый рендер (SDK может прогреться
  // не сразу; в заголовки запросов должна попасть уже реальная строка).
  const TG_INIT_DATA = getTgInitData() || '';

  // Только НАСТОЯЩИЙ Telegram id — без фейкового '1'. Дорезолвим, если Telegram
  // отдаёт id с задержкой; пока пусто — запросы с id не уходят (есть guard'ы).
  const [user_id, setUserId] = useState(() => getTgUserId() || '');
  useEffect(() => {
    if (getTgUserId()) return;
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      const real = getTgUserId();
      if (real) { setUserId(real); clearInterval(iv); }
      else if (n >= 18) clearInterval(iv);
    }, 400);
    return () => clearInterval(iv);
  }, []);

  // Sanitize numeric fields: allow digits and a single dot (also convert commas to dot)
  const sanitizeAmount = (val) => {
    let v = String(val ?? '').replace(/,/g, '.');
    // keep only digits and dots
    v = v.replace(/[^0-9.]/g, '');
    // collapse multiple dots into one
    const parts = v.split('.');
    if (parts.length > 2) {
      v = parts[0] + '.' + parts.slice(1).join('');
    }
    return v;
  };

  const validateOnchainAddress = (addr) => {
    try {
      if (!addr) return false;
      let s = String(addr).trim();
      // allow URL-safe Base64 and fix padding to multiple of 4
      s = s.replace(/-/g, '+').replace(/_/g, '/');
      s += '='.repeat((4 - (s.length % 4)) % 4);
      let raw;
      try { raw = atob(s); } catch (_) {
        try { raw = Buffer.from(s, 'base64').toString('utf-8'); } catch { return false; }
      }
      // Expect exactly three numeric IDs joined by underscores
      return /^\d+_\d+_\d+$/.test(raw);
    } catch { return false; }
  };

  // --- On-chain deposit: реальный адрес TRC20/BEP20 из бэкенда (PAYLOAD) ---
  const [onchainNetwork, setOnchainNetwork] = useState('TRC20'); // 'TRC20' | 'BEP20'
  const [onchainInfo, setOnchainInfo] = useState(null);          // {address, contract, network, min_usd}
  const [onchainLoading, setOnchainLoading] = useState(false);
  const [onchainError, setOnchainError] = useState('');

  useEffect(() => {
    if (!depositModalOpen || depositMode !== 'onchain' || !user_id) return;
    let cancelled = false;
    setOnchainLoading(true);
    setOnchainError('');
    setOnchainInfo(null);
    fetch(`${URL_API}/deposit/address?user_id=${encodeURIComponent(user_id)}&network=${onchainNetwork}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data && data.ok && data.address) setOnchainInfo(data);
        else setOnchainError((data && data.message) || 'Не удалось получить адрес');
      })
      .catch(() => { if (!cancelled) setOnchainError('Сеть недоступна'); })
      .finally(() => { if (!cancelled) setOnchainLoading(false); });
    return () => { cancelled = true; };
  }, [depositModalOpen, depositMode, onchainNetwork, user_id]);


  // Subscribe to shared WS channels
  useEffect(() => {
    if (!wsBalance) return;
    const unsub = wsBalance.addListener((payload) => {
      try {
        if (payload && payload.balances && Object.keys(payload.balances).length === 0) {
          setBalance({ balances: { USDT: 0 }, user_id: payload.user_id || '123' });
        } else {
          setBalance(payload);
        }
      } catch (e) {
        console.error('WS balance parse error:', e);
      }
    });
    // request fresh balance
    wsBalance.send('get');
    return () => { unsub && unsub(); };
  }, [wsBalance]);

  useEffect(() => {
    if (!wsRate) return;
    const unsub = wsRate.addListener((payload) => {
      try { setTokens(payload); } catch (e) { console.error('WS rate parse error:', e); }
    });
    // request fresh rates
    wsRate.send('get');
    return () => { unsub && unsub(); };
  }, [wsRate]);
  // Копирование в буфер обмена
  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const tmp = document.createElement('textarea');
        tmp.value = text;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  // Обработка отправки формы депозита
  const handleDepositSubmit = () => {
    fetch(`${URL_API}/deposit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'initdata': TG_INIT_DATA },
      body: JSON.stringify({
        type: 'deposit',
        user_id: user_id,
        token: depositToken,
        amount: depositField2,
        network: depositField3,
        address: depositField4,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log('Deposit response:', data);
        setDepositModalOpen(false);
        setConfirmationModalOpen(true);
        // Очистка полей
        setDepositToken('');
        setDepositSearchQuery('');
        setDepositField2('');
        setDepositField3('');
        setDepositField4('');
        // Обновляем баланс через WS
        try { wsBalance && wsBalance.send('get'); } catch {}
      })
      .catch((err) => console.error('Ошибка депозита:', err));
  };

  // Обработка отправки формы вывода
  const handleWithdrawSubmit = () => {
    setWithdrawError('');
    setWithdrawSuccess('');

    // Amount and balance guards (anti-overdraft)
    const amt = parseFloat(withdrawField2);
    const available = Number(balance?.balances?.[withdrawToken] || 0);
    if (!isFinite(amt) || amt <= 0) {
      setWithdrawError('Укажите корректную сумму');
      return;
    }
    if (amt > available) {
      setWithdrawError('Сумма превышает доступный баланс');
      return;
    }

    if (!isWithdrawFormValid) {
      setWithdrawError('Пожалуйста, заполните все поля корректно');
      return;
    }

    if (withdrawMode === 'onchain' && !validateOnchainAddress(withdrawField4)) {
      setWithdrawError('Неверный ончейн-адрес.');
      return;
    }

    fetch(`${URL_API}/withdraw`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'initData': TG_INIT_DATA },
      body: JSON.stringify({
        type: 'withdraw',
        user_id: user_id,
        token: withdrawToken,
        amount: withdrawField2,
        ...(withdrawMode === 'direct' ? { network: withdrawField3 } : {}),
        address: withdrawField4,
        mode: withdrawMode,
        ...(withdrawMode === 'onchain' && withdrawExchangeId
          ? { exchange_id: withdrawExchangeId, exchange: listExchange[withdrawExchangeId] }
          : {})
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail = data?.detail || data || {};
          const msg = detail.message || detail.error || 'Ошибка при выводе';
          throw new Error(msg);
        }
        return data;
      })
      .then((data) => {
        try { wsBalance && wsBalance.send('get'); } catch {}

        if (withdrawMode === 'onchain') {
          // Закрываем модалку вывода и показываем подтверждение
          setConfirmationText('Withdrawal sent. Funds will be credited after network confirmation.');
          setWithdrawModalOpen(false);
          setConfirmationModalOpen(true);
        } else {
          // DIRECT: модалка остаётся открытой, показываем inline-успех
          setWithdrawSuccess('Withdrawal request submitted.');
        }

        // Очистка полей формы
        setWithdrawToken('');
        setWithdrawSearchQuery('');
        setWithdrawField2('');
        setWithdrawField3('');
        setWithdrawField4('');
        setWithdrawExchangeId('');
        setWithdrawExSearchQuery('');
        setWithdrawExDropdownOpen(false);
      })
      .catch((err) => {
        const msg = err?.message || 'Ошибка при выводе';
        setWithdrawError(msg);
      });
  };


  useEffect(() => {
    setPageLoaded(true);
  }, []);

  if (!balance?.balances || !tokens) {
    return (
      <>
        {Array.from({ length: 6 }).map((_, idx) => (
          <SkeletonTokenCard key={idx} />
        ))}
      </>
    );
  }

  return (
    <div style={{ 
      opacity: pageLoaded ? 1 : 0, 
      transform: pageLoaded ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)' 
    }}>
      {/* Встроенные стили для анимаций */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes circleDraw {
          to { strokeDashoffset: 0; }
        }
        @keyframes checkDraw {
          to { strokeDashoffset: 0; }
        }
        @keyframes checkmarkDraw {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .fade-in {
          animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .action-btn {
          background-color: #60BDFF;
          color: white;
          padding: 10px 20px;
          border: none;
          border-radius: 30px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .action-btn:active {
          transform: scale(0.98);
        }
        .token-card {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
                      background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .token-card:active {
          transform: scale(0.99);
        }
        .icon-btn {
          background: transparent;
          border: none;
          outline: none;
          cursor: pointer;
          padding: 6px;
          line-height: 0;
          color: white;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), 
                      opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .icon-btn:active { transform: scale(0.94); }
        @keyframes copyPop {
          0% { transform: scale(0.9); opacity: 0.85; }
          60% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); }
        }
        .copy-pop { animation: copyPop 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .modal-overlay {
          animation: fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .modal-content {
          animation: fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>

      <TitleBlock type={'contact'} />

      {/* Основной блок профиля */}
      <div
        style={{
          backgroundColor: '#2A2A33',
          padding: 15,
          height: 500,
          marginTop: 30,
          marginBottom: 20,
          border: '1px solid #3E3E4B',
          borderRadius: 20,
          overflowY: 'auto'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            marginTop: '20px',
            marginBottom: '20px'
          }}
        >
          <button
            className="action-btn fade-in"
            style={{ 
              height: 55, 
              width: '100%', 
              maxWidth: 150,
              fontSize: 16,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
            onClick={() => { setDepositMode('direct'); setDepositModalOpen(true); }}
          >
            <PiHandDepositBold style={{ fontSize: 24 }} /> 
            <span>Deposit</span>
          </button>
          <button
            className="action-btn fade-in"
            style={{ 
              height: 55, 
              width: '100%', 
              maxWidth: 150,
              fontSize: 16,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
            onClick={() => { setWithdrawMode('direct'); setWithdrawModalOpen(true); }}
          >
            <PiHandWithdrawBold style={{ fontSize: 24 }} /> 
            <span>Withdraw</span>
          </button>
        </div>

        <div style={{ marginTop: 15, marginBottom: 15, display: 'flex', justifyContent: 'center' }}>
          <h1 style={{ color: 'white' }}>Your token</h1>
        </div>

        <div>
          {Object.keys(balance.balances).map(currency => {
            const raw = balance.balances[currency];
            const price = tokens?.[currency]?.sell?.price ?? 0;
            const icon = iconPath(currency);

            return (
              <TokenCard
                key={currency}
                currency={currency}
                rawBalance={raw}
                price={price}
                icon={icon}
              />
            );
          })}
        </div>
      </div>

      {/* Модальное окно депозита */}
      {depositModalOpen && (
        <div 
          className="modal-overlay"
          style={modalOverlayStyle}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setDepositModalOpen(false);
            }
          }}
        >
          <div 
            className="modal-content"
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDepositModalOpen(false)}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
              }}
            >
              ×
            </button>

            <h2 style={{ color: 'white', marginBottom: 14 }}>Deposit</h2>

            {/* Segmented control: Напрямую / Ончейн */}
            <div
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
                backgroundColor: '#1F1F25',
                border: '1px solid #3E3E4B',
                borderRadius: 12,
                padding: 4,
              }}
            >
              <button
                onClick={() => setDepositMode('direct')}
                style={{
                  flex: 1,
                  height: 36,
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: depositMode === 'direct' ? '#60BDFF' : 'transparent',
                  color: 'white',
                  fontWeight: 600,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Напрямую
              </button>
              <button
                onClick={() => setDepositMode('onchain')}
                style={{
                  flex: 1,
                  height: 36,
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: depositMode === 'onchain' ? '#60BDFF' : 'transparent',
                  color: 'white',
                  fontWeight: 600,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Ончейн
              </button>
            </div>

            {/* DIRECT MODE (original form) */}
            {depositMode === 'direct' && (
              <>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '25px' }}>
                  <div style={{ position: 'relative', flex: '0 0 140px' }}>
                    <div
                      onClick={() => setDepositDropdownOpen(!depositDropdownOpen)}
                      style={{
                        width: '100%',
                        border: '1px solid #3E3E4B',
                        borderRadius: 5,
                        padding: '0 10px',
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        color: 'white',
                      }}
                    >
                      {depositToken ? (tokens?.[depositToken]?.title || depositToken) : 'Select token'}
                    </div>
                    {depositDropdownOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 45,
                          left: 0,
                          right: 0,
                          backgroundColor: '#1F1F25',
                          border: '1px solid #3E3E4B',
                          borderRadius: 5,
                          zIndex: 10,
                          maxHeight: 350,
                          width: 350,
                          overflowY: 'auto'
                        }}
                      >
                        <div>
                          <input
                            type="text"
                            placeholder="Поиск токена"
                            value={depositSearchQuery}
                            onChange={(e) => setDepositSearchQuery(e.target.value)}
                            style={{
                              width: '100%',
                              backgroundColor: '#2D2D33',
                              border: 'none',
                              outline: 'none',
                              color: 'white',
                              padding: '5px 10px',
                              height: 40
                            }}
                          />
                        </div>
                        <div style={{ marginTop: 5 }}></div>

                        <div>
                          {Object.entries(tokens)
                            .filter(([key, info]) => {
                              if (!depositSearchQuery) return true;
                              const query = depositSearchQuery.toLowerCase();
                              return info.title.toLowerCase().includes(query) || key.toLowerCase().includes(query);
                            })
                            .map(([key, info]) => (
                              <div
                                key={key}
                                onClick={() => {
                                  setDepositToken(key);
                                  setDepositDropdownOpen(false);
                                  setDepositSearchQuery('');
                                }}
                                style={{
                                  padding: '8px 10px',
                                  cursor: 'pointer',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center' }}>

                                  <img
                                    src={iconPath(key)}
                                    alt={info.title}
                                    style={{ height: 28, marginRight: 10 }}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />

                                  {info.title}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Amount"
                    value={depositField2}
                    onChange={(e) => setDepositField2(sanitizeAmount(e.target.value))}
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    style={{
                      width: 100,
                      flex: 1,
                      height: 40,
                      backgroundColor: 'transparent',
                      border: '1px solid #3E3E4B',
                      borderRadius: 5,
                      padding: '0 10px',
                      color: 'white',
                      fontSize: 20
                    }}
                  />
                </div>

                <input
                  type="text"
                  placeholder="Write network"
                  value={depositField3}
                  onChange={(e) => setDepositField3(e.target.value)}
                  style={{
                    marginTop: -20,
                    width: '100%',
                    height: 40,
                    backgroundColor: 'transparent',
                    border: '1px solid #3E3E4B',
                    borderRadius: 5,
                    padding: '0 10px',
                    color: 'white',
                    fontSize: 20,
                    marginBottom: '10px'
                  }}
                />
                <input
                  type="text"
                  placeholder="Write address"
                  value={depositField4}
                  onChange={(e) => setDepositField4(e.target.value)}
                  style={{
                    marginTop: 5,
                    width: '100%',
                    height: 40,
                    backgroundColor: 'transparent',
                    border: '1px solid #3E3E4B',
                    borderRadius: 5,
                    padding: '0 10px',
                    color: 'white',
                    fontSize: 20,
                  }}
                />

                <button
                  onClick={onSubmit}
                  disabled={isDisabled}
                  className="btn"
                  style={{
                    marginTop: 10,
                    color: 'white',
                    backgroundColor: isDisabled ? '#3E3E4B' : '#60BDFF',
                    border: 'none',
                    width: '100%',
                    height: 50,
                    fontSize: 18,
                    fontWeight: 600,
                    marginBottom: '10px',
                    borderRadius: 15,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseDown={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.transform = 'scale(0.98)';
                    }
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Deposit
                </button>
              </>
            )}

            {/* ONCHAIN MODE: show wallet address + copy */}
            {depositMode === 'onchain' && (
              <>
                {/* Выбор сети: USDT TRC20 / BEP20 */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {['TRC20', 'BEP20'].map((net) => (
                    <button
                      key={net}
                      type="button"
                      onClick={() => setOnchainNetwork(net)}
                      style={{
                        flex: 1,
                        padding: '10px 0',
                        borderRadius: 8,
                        border: `1px solid ${onchainNetwork === net ? '#60BDFF' : '#3E3E4B'}`,
                        backgroundColor: onchainNetwork === net ? 'rgba(96,189,255,0.12)' : 'transparent',
                        color: onchainNetwork === net ? '#60BDFF' : '#9CA3AF',
                        fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      USDT {net}
                    </button>
                  ))}
                </div>

                {onchainLoading && (
                  <div style={{ color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                    Получаем адрес…
                  </div>
                )}
                {onchainError && !onchainLoading && (
                  <div style={{ color: '#F87171', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>
                    {onchainError}
                  </div>
                )}

                {onchainInfo && onchainInfo.address && !onchainLoading && (
                  <>
                    {/* QR-код адреса */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                      <div style={{ background: '#fff', padding: 10, borderRadius: 10 }}>
                        <QRCodeCanvas value={onchainInfo.address} size={148} includeMargin={false} />
                      </div>
                    </div>

                    <div style={{
                      backgroundColor: '#1F1F25',
                      border: '1px solid #3E3E4B',
                      borderRadius: 10,
                      padding: '14px 12px',
                      marginBottom: 12,
                    }}>
                      <div style={{ color: '#9CA3AF', fontSize: 12, marginBottom: 8 }}>
                        Адрес для пополнения · USDT {onchainInfo.network}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                        <code style={{ color: 'white', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, wordBreak: 'break-all' }}>
                          {onchainInfo.address}
                        </code>
                        <button
                          type="button"
                          onClick={() => handleCopy(onchainInfo.address)}
                          className={`icon-btn ${copied ? 'copy-pop' : ''}`}
                          aria-label={copied ? 'Скопировано' : 'Скопировать'}
                          title={copied ? 'Скопировано' : 'Скопировать'}
                        >
                          {copied ? <FiCheck size={22} /> : <FiCopy size={22} />}
                        </button>
                      </div>
                    </div>

                    <div style={{ color: '#9CA3AF', fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
                      Отправляйте <b style={{ color: '#fff' }}>только USDT</b> в сети <b style={{ color: '#fff' }}>{onchainInfo.network}</b> на этот адрес.
                      Минимум — <b style={{ color: '#fff' }}>${onchainInfo.min_usd || 1}</b>.<br />
                      Зачисление произойдёт после подтверждения сети. Другие токены/сети по этому адресу не зачислятся.
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно вывода */}
      {withdrawModalOpen && (
        <div 
          className="modal-overlay"
          style={modalOverlayStyle}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setWithdrawModalOpen(false);
            }
          }}
        >
          <div 
            className="modal-content"
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setWithdrawModalOpen(false)}
              style={{
                position: 'absolute', top: 10, right: 10,
                background: 'transparent', border: 'none',
                color: 'white', fontSize: '24px', cursor: 'pointer'
              }}>
              ×
            </button>
            <h2 style={{ color: 'white', marginBottom: '20px' }}>Withdraw</h2>
            <div
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
                backgroundColor: '#1F1F25',
                border: '1px solid #3E3E4B',
                borderRadius: 12,
                padding: 4,
              }}
            >
              <button
                onClick={() => setWithdrawMode('direct')}
                style={{
                  flex: 1,
                  height: 36,
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: withdrawMode === 'direct' ? '#60BDFF' : 'transparent',
                  color: 'white',
                  fontWeight: 600,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Напрямую
              </button>
              <button
                onClick={() => setWithdrawMode('onchain')}
                style={{
                  flex: 1,
                  height: 36,
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: withdrawMode === 'onchain' ? '#60BDFF' : 'transparent',
                  color: 'white',
                  fontWeight: 600,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Ончейн
              </button>
            </div>

            {withdrawMode === 'direct' && (
              <>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '25px' }}>
                  <div style={{ position: 'relative', flex: '0 0 140px' }}>
                    <div
                      onClick={() => setWithdrawDropdownOpen(!withdrawDropdownOpen)}
                      style={{
                        width: '100%', border: '1px solid #3E3E4B', borderRadius: 5,
                        padding: '0 10px', height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        cursor: 'pointer', color: 'white'
                      }}
                    >
                      {withdrawToken ? (tokens?.[withdrawToken]?.title || withdrawToken) : 'Select token'}
                    </div>
                    {withdrawDropdownOpen && (
                      <div
                        style={{
                          position: 'absolute', top: 45, left: 0, right: 0,
                          backgroundColor: '#1F1F25', border: '1px solid #3E3E4B', borderRadius: 5,
                          zIndex: 10, maxHeight: 350, width: 350, overflowY: 'auto'
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search token"
                          value={withdrawSearchQuery}
                          onChange={e => setWithdrawSearchQuery(e.target.value)}
                          style={{
                            width: '100%', height: 40, padding: '0 10px',
                            backgroundColor: '#2D2D33', border: 'none', outline: 'none',
                            color: 'white'
                          }}
                        />
                        {Object.entries(tokens)
                          .filter(([key, info]) => {
                            if (!balance?.balances?.[key]) return false;
                            if (!withdrawSearchQuery) return true;
                            const q = withdrawSearchQuery.toLowerCase();
                            return info.title.toLowerCase().includes(q) || key.toLowerCase().includes(q);
                          })
                          .map(([key, info]) => (
                            <div
                              key={key}
                              onClick={() => {
                                setWithdrawToken(key);
                                setWithdrawDropdownOpen(false);
                                setWithdrawSearchQuery('');
                              }}
                              style={{
                                padding: '8px 10px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                color: 'white'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <img
                                  src={iconPath(key)}
                                  alt={info.title}
                                  style={{ height: 28, marginRight: 10 }}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                {info.title}
                              </div>
                              <span style={{ fontSize: 12, color: '#60BDFF' }}>
                                {balance.balances[key]}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <input
                    type="text"
                    placeholder="Amount"
                    value={withdrawField2}
                    onChange={e => setWithdrawField2(sanitizeAmount(e.target.value))}
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    style={{
                      width: 100,
                      flex: 1,
                      height: 40,
                      backgroundColor: 'transparent',
                      border: '1px solid #3E3E4B',
                      borderRadius: 5,
                      padding: '0 10px',
                      color: 'white',
                      fontSize: 20
                    }}
                  />
                </div>

                <input
                  type="text"
                  placeholder="Write network"
                  value={withdrawField3}
                  onChange={e => setWithdrawField3(e.target.value)}
                  style={{
                    marginTop: -20,
                    width: '100%',
                    height: 40,
                    backgroundColor: 'transparent',
                    border: '1px solid #3E3E4B',
                    borderRadius: 5,
                    padding: '0 10px',
                    color: 'white',
                    fontSize: 20,
                    marginBottom: '10px'
                  }}
                />

                <input
                  type="text"
                  placeholder="Write address"
                  value={withdrawField4}
                  onChange={e => setWithdrawField4(e.target.value)}
                  style={{
                    marginTop: 5,
                    width: '100%',
                    height: 40,
                    backgroundColor: 'transparent',
                    border: '1px solid #3E3E4B',
                    borderRadius: 5,
                    padding: '0 10px',
                    color: 'white',
                    fontSize: 20,
                  }}
                />

                <button
                  disabled={!isWithdrawFormValid}
                  onClick={handleWithdrawSubmit}
                  className="btn"
                  style={{
                    marginTop: 10,
                    color: 'white',
                    backgroundColor: !isWithdrawFormValid ? '#3E3E4B' : '#60BDFF',
                    border: 'none',
                    width: '100%',
                    height: 50,
                    fontSize: 18,
                    fontWeight: 600,
                    marginBottom: '10px',
                    borderRadius: 15,
                    cursor: !isWithdrawFormValid ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseDown={(e) => {
                    if (isWithdrawFormValid) {
                      e.currentTarget.style.transform = 'scale(0.98)';
                    }
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Send
                </button>
                {withdrawSuccess && (
                  <div style={{ 
                    color: '#3ECF8E', 
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    animation: 'fadeIn 0.3s ease',
                  }}>
                    <svg 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      style={{ animation: 'checkmarkDraw 0.5s ease forwards' }}
                    >
                      <circle 
                        cx="12" 
                        cy="12" 
                        r="10" 
                        stroke="#3ECF8E" 
                        strokeWidth="2" 
                        fill="none"
                        style={{ 
                          strokeDasharray: 63,
                          strokeDashoffset: 63,
                          animation: 'circleDraw 0.4s ease forwards'
                        }}
                      />
                      <path 
                        d="M7 12l3 3 7-7" 
                        stroke="#3ECF8E" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        fill="none"
                        style={{ 
                          strokeDasharray: 15,
                          strokeDashoffset: 15,
                          animation: 'checkDraw 0.3s ease 0.2s forwards'
                        }}
                      />
                    </svg>
                    {withdrawSuccess}
                  </div>
                )}
              </>
            )}

            {withdrawMode === 'onchain' && (
              <>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '25px' }}>
                  <div style={{ position: 'relative', flex: '0 0 140px' }}>
                    <div
                      onClick={() => setWithdrawDropdownOpen(!withdrawDropdownOpen)}
                      style={{
                        width: '100%', border: '1px solid #3E3E4B', borderRadius: 5,
                        padding: '0 10px', height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        cursor: 'pointer', color: 'white'
                      }}
                    >
                      {withdrawToken ? (tokens?.[withdrawToken]?.title || withdrawToken) : 'Select token'}
                    </div>
                    {withdrawDropdownOpen && (
                      <div
                        style={{
                          position: 'absolute', top: 45, left: 0, right: 0,
                          backgroundColor: '#1F1F25', border: '1px solid #3E3E4B', borderRadius: 5,
                          zIndex: 10, maxHeight: 350, width: 350, overflowY: 'auto'
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search token"
                          value={withdrawSearchQuery}
                          onChange={e => setWithdrawSearchQuery(e.target.value)}
                          style={{
                            width: '100%', height: 40, padding: '0 10px',
                            backgroundColor: '#2D2D33', border: 'none', outline: 'none',
                            color: 'white'
                          }}
                        />
                        {Object.entries(tokens)
                          .filter(([key, info]) => {
                            if (!balance?.balances?.[key]) return false;
                            if (!withdrawSearchQuery) return true;
                            const q = withdrawSearchQuery.toLowerCase();
                            return info.title.toLowerCase().includes(q) || key.toLowerCase().includes(q);
                          })
                          .map(([key, info]) => (
                            <div
                              key={key}
                              onClick={() => {
                                setWithdrawToken(key);
                                setWithdrawDropdownOpen(false);
                                setWithdrawSearchQuery('');
                              }}
                              style={{
                                padding: '8px 10px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                color: 'white'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <img
                                  src={iconPath(key)}
                                  alt={info.title}
                                  style={{ height: 28, marginRight: 10 }}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                {info.title}
                              </div>
                              <span style={{ fontSize: 12, color: '#60BDFF' }}>
                                {balance.balances[key]}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <input
                    type="text"
                    placeholder="Amount"
                    value={withdrawField2}
                    onChange={e => setWithdrawField2(sanitizeAmount(e.target.value))}
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    style={{
                      width: 100,
                      flex: 1,
                      height: 40,
                      backgroundColor: 'transparent',
                      border: '1px solid #3E3E4B',
                      borderRadius: 5,
                      padding: '0 10px',
                      color: 'white',
                      fontSize: 20
                    }}
                  />
                </div>

                {/* Select exchange (like token selector) */}
                <div style={{ position: 'relative', marginTop: -10, marginBottom: 15 }}>
                  <div
                    onClick={() => setWithdrawExDropdownOpen(!withdrawExDropdownOpen)}
                    style={{
                      width: '100%',
                      border: '1px solid #3E3E4B',
                      borderRadius: 5,
                      padding: '0 10px',
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      color: 'white',
                      backgroundColor: 'transparent'
                    }}
                  >
                    {withdrawExchangeId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img
                          src={selectIconExchange(withdrawExchangeId)}
                          alt={listExchange[withdrawExchangeId]}
                          style={{ width: 24, height: 24, borderRadius: 12, border: '1px solid #3E3E4B' }}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/access/indexExchange/0.svg'; }}
                        />
                        {listExchange[withdrawExchangeId]}
                      </div>
                    ) : 'Select exchange'}
                  </div>

                  {withdrawExDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 45,
                        left: 0,
                        right: 0,
                        backgroundColor: '#1F1F25',
                        border: '1px solid #3E3E4B',
                        borderRadius: 5,
                        zIndex: 10,
                        maxHeight: 350,
                        overflowY: 'auto'
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Search exchange"
                        value={withdrawExSearchQuery}
                        onChange={(e) => setWithdrawExSearchQuery(e.target.value)}
                        style={{
                          width: '100%',
                          backgroundColor: '#2D2D33',
                          border: 'none',
                          outline: 'none',
                          color: 'white',
                          padding: '5px 10px',
                          height: 40
                        }}
                      />

                      {Object.entries(listExchange)
                        .filter(([id, name]) => {
                          if (!withdrawExSearchQuery) return true;
                          const q = withdrawExSearchQuery.toLowerCase();
                          return name.toLowerCase().includes(q) || id.toLowerCase().includes(q);
                        })
                        .map(([id, name]) => (
                          <div
                            key={id}
                            onClick={() => {
                              setWithdrawExchangeId(id);
                              setWithdrawExDropdownOpen(false);
                              setWithdrawExSearchQuery('');
                            }}
                            style={{
                              padding: '8px 10px',
                              cursor: 'pointer',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <img
                                src={selectIconExchange(id)}
                                alt={name}
                                style={{ width: 28, height: 28, borderRadius: 14, border: '1px solid #3E3E4B' }}
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/access/indexExchange/0.svg'; }}
                              />
                              {name}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  placeholder="Write address"
                  value={withdrawField4}
                  onChange={e => setWithdrawField4(e.target.value)}
                  style={{
                    marginTop: 5,
                    width: '100%',
                    height: 40,
                    backgroundColor: 'transparent',
                    border: '1px solid #3E3E4B',
                    borderRadius: 5,
                    padding: '0 10px',
                    color: 'white',
                    fontSize: 20,
                  }}
                />

                <button
                  disabled={!isWithdrawFormValid}
                  onClick={handleWithdrawSubmit}
                  className="btn"
                  style={{
                    marginTop: 10,
                    color: 'white',
                    backgroundColor: !isWithdrawFormValid ? '#3E3E4B' : '#60BDFF',
                    border: 'none',
                    width: '100%',
                    height: 50,
                    fontSize: 18,
                    fontWeight: 600,
                    marginBottom: '10px',
                    borderRadius: 15,
                    cursor: !isWithdrawFormValid ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseDown={(e) => {
                    if (isWithdrawFormValid) {
                      e.currentTarget.style.transform = 'scale(0.98)';
                    }
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Send
                </button>
              </>
            )}

            {withdrawError && (
              <p style={{ color: 'rgb(255, 96, 99)', marginTop: '10px' }}>
                {withdrawError}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения */}
      {confirmationModalOpen && (
        <div 
          className="modal-overlay"
          style={modalOverlayStyle}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setConfirmationModalOpen(false);
            }
          }}
        >
          <div 
            className="modal-content"
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ color: 'white' }}>{confirmationText}</p>
            <button
              onClick={() => setConfirmationModalOpen(false)}
              style={{
                marginTop: 10,
                color: 'white',
                border: '2px solid #3E3E4B',
                width: '100%',
                height: 50,
                fontSize: 18,
                fontWeight: 600,
                borderRadius: 15,
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Закрыть
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileCard;
